import React, { useEffect, useState } from 'react';
import DashboardCard from '@/components/ui/DashboardCard';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { useImageLibraryData } from './hooks/useImageLibraryData';
import { ImageLibraryContent } from './ImageLibraryContent';
import { ImageDetail } from '@/components/image-lightbox';
import { ImageLibraryItem } from '@/hooks/runware/image-library';
import { PublicImageMetadataSidebar } from './components/PublicImageMetadataSidebar';
import { useUser } from '@/hooks/auth/use-user';

interface ImageLibraryContainerProps {
  styleId?: string;
  influenceId?: string;
  backgroundId?: string;
  environmentIds?: string[];
  moodIds?: string[];
  colorIds?: string[];
  imageType?: string;
  description?: string;
  debouncedDescription?: string;
  width?: string;
  height?: string;
  // Controlled component support
  selectedImage?: ImageLibraryItem | null;
  onImageSelect?: (image: ImageLibraryItem | null) => void;
}

// Single consolidated component - no more wrapper layers
export const ImageLibraryContainer: React.FC<ImageLibraryContainerProps> = ({
  selectedImage: propSelectedImage,
  onImageSelect: propOnImageSelect,
  debouncedDescription,
  ...props
}) => {
  // Get current user
  const { user } = useUser();
  
  // Internal state for uncontrolled mode
  const [internalSelectedImage, setInternalSelectedImage] = useState<ImageLibraryItem | null>(null);
  const [showOnlyUserImages, setShowOnlyUserImages] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [description, setDescription] = useState(props.description || '');

  // Controlled vs uncontrolled pattern - single source of truth
  const selectedImage = propSelectedImage !== undefined ? propSelectedImage : internalSelectedImage;
  const setSelectedImage = propOnImageSelect || setInternalSelectedImage;

  // Parse dimensions for API
  const parsedWidth = props.width ? parseInt(props.width) : undefined;
  const parsedHeight = props.height ? parseInt(props.height) : undefined;

  // Update description when props change
  useEffect(() => {
    if (props.description !== undefined) {
      setDescription(props.description);
    }
  }, [props.description]);

  // Build filters object
  const filters = {
    styleId: props.styleId ?? undefined,
    influenceId: props.influenceId ?? undefined,
    backgroundId: props.backgroundId ?? undefined,
    environmentIds: props.environmentIds,
    moodIds: props.moodIds,
    colorIds: props.colorIds,
    imageType: props.imageType,
    description: debouncedDescription || description,
    width: parsedWidth,
    height: parsedHeight,
  };

  // Single data hook - no context complexity
  const {
    images,
    isLoading,
    isError,
    loadMore,
    isFetchingNextPage,
    hasNextPage,
    handleDeleteImage,
    searchTerm
  } = useImageLibraryData(filters, showOnlyUserImages, showFavorites);

  // Intersection observer for infinite scrolling
  const [loadMoreRef] = useIntersectionObserver({
    onIntersect: loadMore,
    enabled: !!hasNextPage && !isFetchingNextPage,
    rootMargin: '200px'
  });

  // Custom delete handler for ImageDetail
  const handleCustomDelete = async (image: ImageLibraryItem): Promise<void> => {
    await handleDeleteImage(image.id);
  };

  // Custom download handler for ImageDetail
  const handleCustomDownload = async (image: ImageLibraryItem): Promise<void> => {
    try {
      // Create download link
      const link = document.createElement('a');
      link.href = image.image_url;
      link.download = `image-${image.id}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  // Property accessors for ImageDetail
  const getImageUrl = (item: ImageLibraryItem): string => item.image_url;
  const getImageId = (item: ImageLibraryItem): string => item.id;
  const getImageDescription = (item: ImageLibraryItem): string | undefined => 
    item.description || item.original_prompt || undefined;
  const getUserId = (item: ImageLibraryItem): string | undefined => item.user_id;
  const getFavoriteCount = (item: ImageLibraryItem): number | undefined => item.favorite_count;
  
  // Custom ownership check - public images have user_id
  const checkOwnership = (item: ImageLibraryItem): boolean => {
    // For public images, check if current user owns the image
    if (!user?.id || !item.user_id) {
      return false;
    }
    return user.id === item.user_id;
  };
  
  // Custom sidebar renderer for Public Image Generator
  const renderCustomSidebar = (image: ImageLibraryItem, context: any) => {
    return (
      <PublicImageMetadataSidebar
        image={image}
        isOwner={context.isOwner}
        isDeleteLoading={context.isDeleteLoading}
        onDownload={context.onDownload}
        onDelete={context.onDelete}
        hasPrevious={context.hasPrevious}
        hasNext={context.hasNext}
        isLoadingMore={context.isLoadingMore}
        hasNextPage={context.hasNextPage}
        onPrevious={context.onPrevious}
        onNext={context.onNext}
        onLoadMore={context.onLoadMore}
      />
    );
  };

  return (
    <DashboardCard variant="purple" className="p-3 md:p-6">
      <ImageLibraryContent
        images={images}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        loadMoreRef={loadMoreRef}
        onDeleteImage={handleDeleteImage}
        onSelectImage={setSelectedImage}
        showFavorites={showFavorites}
        showOnlyUserImages={showOnlyUserImages}
        onFilterChange={{ setShowFavorites, setShowOnlyUserImages }}
        searchTerm={searchTerm || debouncedDescription || description || ''}
        parsedWidth={parsedWidth}
        parsedHeight={parsedHeight}
      />
      
      {/* Enhanced ImageDetail with custom sidebar that includes Storage, Project, and Favorite buttons */}
      <ImageDetail<ImageLibraryItem>
        image={selectedImage}
        isOpen={!!selectedImage}
        onClose={() => setSelectedImage(null)}
        allImages={images.filter(img => img !== null)}
        onNavigate={setSelectedImage}
        loadMore={loadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        
        // Property accessors for ImageLibraryItem
        getImageUrl={getImageUrl}
        getImageId={getImageId}
        getImageDescription={getImageDescription}
        getUserId={getUserId}
        getFavoriteCount={getFavoriteCount}
        getIsFavorited={(item: ImageLibraryItem) => item.is_favorite || false} // NEW: Pass individual favorite status
        
        // Custom handlers
        onDelete={handleCustomDelete}
        onDownload={handleCustomDownload}
        checkOwnership={checkOwnership}
        
        // Custom sidebar that includes Storage, Project, and Favorite management
        renderSidebar={renderCustomSidebar}
        
        // Display options - enable favorites for public images
        showFavoriteButton={true}
        showDescription={true}
        dropdownLabel="Public Image"
      />
    </DashboardCard>
  );
};
